import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api, { extrairMensagemErro, storageKeys, SessaoTipo } from '../services/api';
import { storage } from '../services/storage';
import { carregarUsuarioSessao } from '../permissions';

type LoginProps = {
  onLogin: (dados: any, tipo: SessaoTipo) => void;
};

type ModoTela = 'login' | 'recuperar' | 'candidatura';
type TipoCandidatura = 'EMPRESA' | 'FORNECEDOR';

const ficheiroParaBase64 = (ficheiro: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const leitor = new FileReader();
    leitor.onload = () => resolve(String(leitor.result || '').split(',')[1] || '');
    leitor.onerror = () => reject(new Error('Não foi possível ler o ficheiro.'));
    leitor.readAsDataURL(ficheiro);
  });

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [modo, setModo] = useState<ModoTela>('login');
  const [tipo, setTipo] = useState<SessaoTipo>('usuario');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [emailRecuperacao, setEmailRecuperacao] = useState('');
  const [tipoRecuperacao, setTipoRecuperacao] = useState<SessaoTipo>('usuario');
  const [tipoCandidatura, setTipoCandidatura] = useState<TipoCandidatura>('EMPRESA');
  const [candidatura, setCandidatura] = useState<Record<string, string>>({});
  const [documento, setDocumento] = useState<File | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [mensagem, setMensagem] = useState<string | null>(null);

  const limparFeedback = () => {
    setErro(null);
    setMensagem(null);
  };

  const submeter = async (evento: React.FormEvent) => {
    evento.preventDefault();
    setCarregando(true);
    limparFeedback();

    try {
      const endpoint = tipo === 'fornecedor' ? '/fornecedores/login' : '/auth/login';
      const resposta = await api.post(endpoint, { email, senha });
      const dados = resposta.data.dados;
      const usuario = tipo === 'fornecedor'
        ? { ...dados.fornecedor, perfil: 'FORNECEDOR', vinculos: dados.vinculos }
        : { ...dados.usuario, tenant: dados.tenant, empresa: dados.tenant?.nome_empresa };

      await storage.setItem(storageKeys.token, dados.token);
      await storage.setItem(storageKeys.usuario, JSON.stringify(usuario));
      await storage.setItem(storageKeys.tipo, tipo);

      if (dados.usuario?.tenant_id || dados.tenant?.id) {
        await storage.setItem(storageKeys.tenant, dados.usuario?.tenant_id || dados.tenant?.id);
      }

      await carregarUsuarioSessao();
      onLogin(usuario, tipo);
    } catch (erroLogin: any) {
      setErro(extrairMensagemErro(erroLogin, 'Não foi possível iniciar sessão.'));
    } finally {
      setCarregando(false);
    }
  };

  const recuperarSenha = async (evento: React.FormEvent) => {
    evento.preventDefault();
    setCarregando(true);
    limparFeedback();

    try {
      const resposta = await api.post('/auth/forgot-password', { email: emailRecuperacao, tipo: tipoRecuperacao });
      setMensagem(resposta.data.mensagem || 'Pedido de recuperação registado.');
    } catch (erroRecuperacao: any) {
      setErro(extrairMensagemErro(erroRecuperacao, 'Não foi possível solicitar recuperação de senha.'));
    } finally {
      setCarregando(false);
    }
  };

  const atualizarCandidatura = (campo: string, valor: string) => {
    setCandidatura((atual) => ({ ...atual, [campo]: valor }));
  };

  const submeterCandidatura = async (evento: React.FormEvent) => {
    evento.preventDefault();
    setCarregando(true);
    limparFeedback();

    try {
      if (!documento) {
        setErro('Anexe um documento PDF, JPEG ou JPG antes de submeter.');
        return;
      }

      const conteudoBase64 = await ficheiroParaBase64(documento);
      const payload = {
        tipo: tipoCandidatura,
        nome_empresa: candidatura.nome_empresa,
        cnpj_nif: tipoCandidatura === 'EMPRESA' ? candidatura.cnpj_nif : undefined,
        nif: tipoCandidatura === 'FORNECEDOR' ? candidatura.nif : undefined,
        email: candidatura.email,
        telefone: candidatura.telefone,
        gerente_nome: tipoCandidatura === 'EMPRESA' ? candidatura.gerente_nome : undefined,
        gerente_email: tipoCandidatura === 'EMPRESA' ? candidatura.gerente_email : undefined,
        senha: candidatura.senha,
        documento: {
          nome_original: documento.name,
          mime_type: documento.type || (documento.name.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'image/jpeg'),
          conteudo_base64: conteudoBase64
        }
      };

      const resposta = await api.post('/candidaturas', payload);
      setMensagem(resposta.data.mensagem || 'Candidatura submetida com sucesso.');
      setCandidatura({});
      setDocumento(null);
    } catch (erroCandidatura: any) {
      setErro(extrairMensagemErro(erroCandidatura, 'Não foi possível submeter candidatura.'));
    } finally {
      setCarregando(false);
    }
  };

  return (
    <main className="login-page">
      <section className="login-panel">
        <div className="brand-lockup">
          <img className="brand-mark" src="/assets/logotipo-akzicao.png" alt="Akzição" />
          <div>
            <h1>Akzição</h1>
            <p>Compras, aprovações e fornecedores num único fluxo operacional.</p>
          </div>
        </div>

        {modo === 'login' && (
          <>
            <div className="segmented-control" aria-label="Tipo de acesso">
              <button type="button" className={tipo === 'usuario' ? 'active' : ''} onClick={() => setTipo('usuario')}>
                Equipa interna
              </button>
              <button type="button" className={tipo === 'fornecedor' ? 'active' : ''} onClick={() => setTipo('fornecedor')}>
                Fornecedor
              </button>
            </div>

            <form className="login-form" onSubmit={submeter}>
              <label>
                E-mail
                <input type="email" value={email} onChange={(evento) => setEmail(evento.target.value)} placeholder="nome@empresa.com" autoComplete="email" required />
              </label>

              <label>
                Senha
                <input type="password" value={senha} onChange={(evento) => setSenha(evento.target.value)} placeholder="A sua senha" autoComplete="current-password" required />
              </label>

              {mensagem && <div className="alerta alerta-sucesso">{mensagem}</div>}
              {erro && <div className="alerta alerta-erro">{erro}</div>}

              <button className="botao botao-primario" type="submit" disabled={carregando}>
                {carregando ? 'A entrar...' : 'Entrar'}
              </button>
            </form>

            <div className="auth-actions">
              <button type="button" className="botao botao-secundario" onClick={() => { limparFeedback(); setModo('recuperar'); }}>
                Recuperar senha
              </button>
              <button type="button" className="botao botao-secundario" onClick={() => { limparFeedback(); setModo('candidatura'); }}>
                Submeter candidatura à plataforma
              </button>
            </div>
            <div className="login-public-footer" style={{ marginTop: '1.5rem', textAlign: 'center' }}>
              <Link to="/privacy-policy" style={{ color: '#d4af37', fontSize: '0.85rem', textDecoration: 'underline' }}>
                Política de Privacidade da Akzição
              </Link>
            </div>
          </>
        )}

        {modo === 'recuperar' && (
          <form className="login-form" onSubmit={recuperarSenha}>
            <h2>Recuperar senha</h2>
            <div className="segmented-control" aria-label="Tipo de conta para recuperação">
              <button type="button" className={tipoRecuperacao === 'usuario' ? 'active' : ''} onClick={() => setTipoRecuperacao('usuario')}>
                Equipa interna
              </button>
              <button type="button" className={tipoRecuperacao === 'fornecedor' ? 'active' : ''} onClick={() => setTipoRecuperacao('fornecedor')}>
                Fornecedor
              </button>
            </div>
            <label>
              E-mail
              <input type="email" value={emailRecuperacao} onChange={(evento) => setEmailRecuperacao(evento.target.value)} required />
            </label>
            {mensagem && <div className="alerta alerta-sucesso">{mensagem}</div>}
            {erro && <div className="alerta alerta-erro">{erro}</div>}
            <button className="botao botao-primario" type="submit" disabled={carregando}>
              {carregando ? 'A enviar...' : 'Enviar instruções'}
            </button>
            <button type="button" className="botao botao-secundario" onClick={() => { limparFeedback(); setModo('login'); }}>
              Voltar ao login
            </button>
          </form>
        )}

        {modo === 'candidatura' && (
          <form className="login-form" onSubmit={submeterCandidatura}>
            <h2>Candidatura pública</h2>
            <div className="segmented-control" aria-label="Tipo de candidatura">
              <button type="button" className={tipoCandidatura === 'EMPRESA' ? 'active' : ''} onClick={() => setTipoCandidatura('EMPRESA')}>
                Empresa
              </button>
              <button type="button" className={tipoCandidatura === 'FORNECEDOR' ? 'active' : ''} onClick={() => setTipoCandidatura('FORNECEDOR')}>
                Fornecedor
              </button>
            </div>

            <label>
              Nome da empresa
              <input value={candidatura.nome_empresa || ''} onChange={(evento) => atualizarCandidatura('nome_empresa', evento.target.value)} required />
            </label>
            <label>
              {tipoCandidatura === 'EMPRESA' ? 'CNPJ/NIF da empresa' : 'NIF do fornecedor'}
              <input value={tipoCandidatura === 'EMPRESA' ? candidatura.cnpj_nif || '' : candidatura.nif || ''} onChange={(evento) => atualizarCandidatura(tipoCandidatura === 'EMPRESA' ? 'cnpj_nif' : 'nif', evento.target.value)} required={tipoCandidatura === 'EMPRESA'} />
            </label>
            <label>
              E-mail operacional
              <input type="email" value={candidatura.email || ''} onChange={(evento) => atualizarCandidatura('email', evento.target.value)} required />
            </label>
            <label>
              Telefone
              <input value={candidatura.telefone || ''} onChange={(evento) => atualizarCandidatura('telefone', evento.target.value)} />
            </label>

            {tipoCandidatura === 'EMPRESA' && (
              <>
                <label>
                  Nome do gerente inicial
                  <input value={candidatura.gerente_nome || ''} onChange={(evento) => atualizarCandidatura('gerente_nome', evento.target.value)} required />
                </label>
                <label>
                  E-mail do gerente inicial
                  <input type="email" value={candidatura.gerente_email || ''} onChange={(evento) => atualizarCandidatura('gerente_email', evento.target.value)} required />
                </label>
              </>
            )}

            <label>
              Senha inicial
              <input type="password" value={candidatura.senha || ''} onChange={(evento) => atualizarCandidatura('senha', evento.target.value)} minLength={6} required />
            </label>
            <label>
              Documento obrigatório
              <small className="field-hint" style={{ display: 'block', fontSize: '0.82rem', color: '#555', marginTop: '4px', marginBottom: '6px', lineHeight: '1.4' }}>
                Documento de Identificação da Empresa contendo uma página com o Número de Identificação Fiscal (NIF) e uma página com a Certidão de Registo Comercial, Alvará Comercial ou licença equivalente.
              </small>
              <input type="file" accept=".pdf,.jpg,.jpeg,application/pdf,image/jpeg" onChange={(evento) => setDocumento(evento.target.files?.[0] || null)} required />
            </label>

            {mensagem && <div className="alerta alerta-sucesso">{mensagem}</div>}
            {erro && <div className="alerta alerta-erro">{erro}</div>}

            <button className="botao botao-primario" type="submit" disabled={carregando}>
              {carregando ? 'A submeter...' : 'Submeter candidatura'}
            </button>
            <button type="button" className="botao botao-secundario" onClick={() => { limparFeedback(); setModo('login'); }}>
              Voltar ao login
            </button>
          </form>
        )}
      </section>

      <aside className="login-aside">
        <h2>Fluxo pronto para operar</h2>
        <dl>
          <div>
            <dt>Candidatura</dt>
            <dd>Empresas e fornecedores entram por análise documental.</dd>
          </div>
          <div>
            <dt>Aprovação</dt>
            <dd>Super Admin valida documentos antes de liberar acesso.</dd>
          </div>
          <div>
            <dt>Operação</dt>
            <dd>Gerentes administram equipa interna e vínculos do próprio tenant.</dd>
          </div>
        </dl>
      </aside>
    </main>
  );
};

export default Login;
